import { useState } from "react";
import { Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

interface ReportEventModalProps {
  eventId: string;
}

export function ReportEventModal({ eventId }: ReportEventModalProps) {
  const { t } = useTranslation("eventDetail");
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!reason) {
      toast({
        title: t("reportModal.toast.selectReasonTitle"),
        description: t("reportModal.toast.selectReasonDesc"),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id || null;

      const { error } = await supabase
        .from('event_reports' as any)
        .insert([{
          event_id: eventId,
          reporter_id: userId,
          reason,
          details
        }]);

      if (error) throw error;

      toast({
        title: t("reportModal.toast.successTitle"),
        description: t("reportModal.toast.successDesc"),
      });
      setOpen(false);
      setReason("");
      setDetails("");
    } catch (error: any) {
      console.error("Error reporting event:", error);
      toast({
        title: t("reportModal.toast.errorTitle"),
        description: t("reportModal.toast.errorDesc"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive w-fit transition-colors mx-auto flex items-center justify-center gap-2 mt-12 mb-8">
          <Flag className="h-4 w-4" />
          {t("reportModal.triggerButton")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] p-4 sm:p-6">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t("reportModal.title")}</DialogTitle>
            <DialogDescription>
              {t("reportModal.description")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6">
            <div className="space-y-4">
              <Label className="text-base">{t("reportModal.problemLabel")}</Label>
              <RadioGroup value={reason} onValueChange={setReason} className="flex flex-col gap-3">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Fraude ou Golpe" id="fraude" />
                  <Label htmlFor="fraude" className="font-normal">{t("reportModal.reasons.fraud")}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Conteúdo impróprio" id="improprio" />
                  <Label htmlFor="improprio" className="font-normal">{t("reportModal.reasons.inappropriate")}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Spam" id="spam" />
                  <Label htmlFor="spam" className="font-normal">{t("reportModal.reasons.spam")}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Direitos autorais" id="direitos" />
                  <Label htmlFor="direitos" className="font-normal">{t("reportModal.reasons.copyright")}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Outro" id="outro" />
                  <Label htmlFor="outro" className="font-normal">{t("reportModal.reasons.other")}</Label>
                </div>
              </RadioGroup>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="details">{t("reportModal.detailsLabel")}</Label>
              <Textarea 
                id="details" 
                placeholder={t("reportModal.detailsPlaceholder")}
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                className="h-24 resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={isSubmitting}>
              {t("reportModal.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t("reportModal.submitting") : t("reportModal.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}